import { originalPositionFor, TraceMap } from '@jridgewell/trace-mapping';
import { monoid, option, readonlyArray, string, task, taskOption } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import * as iots from 'io-ts';
import { IntFromString } from 'io-ts-types';
import { parseOption, stringJoin } from '../../internal/prelude.js';
/**
 * Maps an error's stack trace using source maps, where available.
 */
export const withMappedStackTrace = (error, fetchFromUri) => async () => {
    if (error.stack != null) {
        error.stack = await mapStackTrace(error.stack, fetchFromUri)();
    }
    if (error.cause instanceof Error) {
        await withMappedStackTrace(error.cause, fetchFromUri)();
    }
};
const SourceMapping = iots.strict({
    source: iots.string,
    line: iots.number,
    column: iots.number,
});
/**
 * Rewrites each line of a stack trace to the original source, if a source map is available
 * for the file listed in the line.
 */
export const mapStackTrace = (stack, fetchFromUri) => pipe(stack, string.split('\n'), readonlyArray.traverse(task.ApplicativePar)((stackLine) => pipe(parseStackLine(stackLine), taskOption.fromOption, taskOption.bind('sourceMap', ({ uri }) => fetchFromUri(uri)), taskOption.chainOptionK(({ sourceMap, line, column }) => option.tryCatch(() => {
    const tracer = new TraceMap(sourceMap);
    return originalPositionFor(tracer, { line, column });
})), taskOption.chainOptionK(parseOption(SourceMapping)), taskOption.map(({ source, line, column }) => `${source}:${line}:${column}`), taskOption.getOrElse(() => task.of(stackLine)))), task.map(monoid.concatAll(stringJoin('\n'))));
/**
 * Parses a single line in a stack trace to extract the URI, line, and column numbers of the error.
 */
export const parseStackLine = (line) => pipe(option.fromNullable(
// eslint-disable-next-line redos/no-vulnerable
/.*(https?:\/\/.*):(\d+):(\d+)/.exec(line)), option.chain(parseOption(iots.tuple([iots.unknown, iots.string, IntFromString, IntFromString]))), option.map(([, uri, line, column]) => ({ uri, line, column })));
/**
 * Given a source file, extract the source mapping URL and map it to a full URI if necessary.
 */
export const extractSourceMappingUrl = (baseUri) => (source) => pipe(option.fromNullable(/\/\/# sourceMappingURL=(.*)/.exec(source)), option.chain(parseOption(iots.tuple([iots.unknown, iots.string]))), option.map(([, filePath]) => new URL(filePath, baseUri).href + new URL(baseUri).search));
//# sourceMappingURL=mapStackTrace.js.map